/**
 * SPReferralModal Component
 * Feature 001: SP Client Management - Provider Referrals
 *
 * Modal for SPs to select clients to share a provider with
 */

import { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Users, Search, CheckCircle2, AlertCircle, X, Loader2 } from 'lucide-react';
import { useClientList } from '../hooks/useClientList';
import { useProviderReferrals } from '../hooks/useProviderReferrals';
import { useShareProvider } from '../hooks/useShareProvider';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { NewClientInvitationForm, type NewClientInvitationFormHandle } from './NewClientInvitationForm';

type SPReferralModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string | null;
  /**
   * Feature 071 — switch to sending this provider's contact card instead of an
   * invitation. Handled by the parent rather than opened here: stacking one
   * Radix dialog inside another fights over the focus trap.
   */
  onSendContactCard?: () => void;
  providerName: string;
  profileViewEngagementId?: string;
};

export function SPReferralModal({
  open,
  onOpenChange,
  providerId,
  providerName,
  onSendContactCard,
  profileViewEngagementId,
}: SPReferralModalProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('existing');
  const [searchTerm, setSearchTerm] = useState('');
  // Feature 102: required attestation + client-name opt-out (default on).
  const [attested, setAttested] = useState(false);
  const [shareClientName, setShareClientName] = useState(true);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(
    new Set(),
  );
  const newClientFormRef = useRef<NewClientInvitationFormHandle>(null);

  const { data: clientsData, isLoading: isLoadingClients } = useClientList(
    user?.id || '',
    { limit: 50 }, // Max allowed by backend validation
  );
  const { data: existingReferrals = [], isLoading: isLoadingReferrals } =
    useProviderReferrals(providerId);
  const { mutate: shareProvider, isPending } = useShareProvider();

  const clients = clientsData?.data || [];

  // Get set of already-referred client IDs
  const alreadyReferredIds = useMemo(() => {
    const ids = new Set<string>();
    existingReferrals.forEach((referral) => {
      referral.clientIds.forEach((clientId) => ids.add(clientId));
    });
    return ids;
  }, [existingReferrals]);

  // Filter clients by search term
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) {
      return clients;
    }

    const term = searchTerm.toLowerCase();
    return clients.filter(
      (client) =>
        `${client.firstName} ${client.lastName}`.toLowerCase().includes(term) ||
        client.email.toLowerCase().includes(term),
    );
  }, [clients, searchTerm]);

  // Get counts
  const totalClients = clients.length;
  const filteredCount = filteredClients.length;
  const selectedCount = selectedClientIds.size;
  const alreadyReferredCount = alreadyReferredIds.size;

  // Handle select all (filtered clients only)
  const handleSelectAll = () => {
    const newSelected = new Set(selectedClientIds);
    filteredClients.forEach((client) => {
      if (!alreadyReferredIds.has(client.id)) {
        newSelected.add(client.id);
      }
    });
    setSelectedClientIds(newSelected);
  };

  // Handle deselect all
  const handleDeselectAll = () => {
    setSelectedClientIds(new Set());
  };

  // Handle individual selection toggle
  const handleToggleClient = (clientId: string) => {
    const newSelected = new Set(selectedClientIds);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClientIds(newSelected);
  };

  // Handle submit
  const handleSubmit = () => {
    if (!providerId || selectedClientIds.size === 0 || !attested) return;

    shareProvider(
      {
        referredProviderId: providerId,
        clientIds: Array.from(selectedClientIds),
        profileViewEngagementId,
        attested,
        shareClientName,
      },
      {
        onSuccess: () => {
          // Reset and close
          setSelectedClientIds(new Set());
          setSearchTerm('');
          setAttested(false);
          setShareClientName(true);
          onOpenChange(false);

          // You could show a toast here if you have a toast system
        },
        onError: (error: unknown) => {
          console.error('Failed to share provider:', error);
        },
      },
    );
  };

  // Handle close
  const handleClose = () => {
    if (!isPending) {
      setSelectedClientIds(new Set());
      setSearchTerm('');
      setActiveTab('existing');
      setAttested(false);
      setShareClientName(true);
      onOpenChange(false);
    }
  };

  // Handle new client invitation success
  const handleNewClientSuccess = () => {
    setActiveTab('existing');
    onOpenChange(false);
  };

  // Handle new client invitation cancel
  const handleNewClientCancel = () => {
    setActiveTab('existing');
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const isLoading = isLoadingClients || isLoadingReferrals;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Share Provider with Clients
          </DialogTitle>
          <DialogDescription>
            Select clients to share <strong>{providerName}</strong> with
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="existing">Existing Clients</TabsTrigger>
            <TabsTrigger value="new">New Client (not in app)</TabsTrigger>
          </TabsList>

          {/* Existing Clients Tab */}
          <TabsContent value="existing" className="flex-1 flex flex-col mt-4 min-h-0">
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search clients by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Stats Bar */}
            <div className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-md mb-4 flex-shrink-0">
              <div className="flex items-center gap-4">
                <span>
                  {filteredCount} of {totalClients} clients
                </span>
                {alreadyReferredCount > 0 && (
                  <span className="text-green-600">
                    {alreadyReferredCount} already shared
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {selectedCount} selected
                </span>
                {selectedCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeselectAll}
                    className="h-7 text-xs"
                  >
                    Deselect All
                  </Button>
                )}
                {filteredCount > 0 && selectedCount < filteredCount - alreadyReferredCount && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                    className="h-7 text-xs"
                  >
                    Select All
                  </Button>
                )}
              </div>
            </div>

            {/* Client List */}
            <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[400px] border rounded-md">
              {isLoading ? (
                <div className="flex items-center justify-center h-40 text-gray-500">
                  Loading clients...
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                  <Users className="w-12 h-12 mb-2 text-gray-300" />
                  <p className="text-sm">
                    {searchTerm ? 'No clients match your search' : 'No connected clients'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredClients.map((client) => {
                    const isSelected = selectedClientIds.has(client.id);
                    const alreadyReferred = alreadyReferredIds.has(client.id);

                    return (
                      <label
                        key={client.id}
                        className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          alreadyReferred ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleClient(client.id)}
                          disabled={alreadyReferred}
                          className="w-4 h-4 rounded border-gray-300 text-[#026857] focus:ring-[#026857] disabled:cursor-not-allowed"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 truncate">
                              {client.firstName} {client.lastName}
                            </p>
                            {alreadyReferred && (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="w-3 h-3" />
                                Already shared
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            {client.email}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info Alert */}
            {totalClients === 0 && !isLoading && (
              <Alert className="mb-4 flex-shrink-0">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You don't have any connected clients yet. Invite clients to connect before sharing providers.
                </AlertDescription>
              </Alert>
            )}

            {/* Feature 102: consent controls */}
            <div className="border-t pt-3 mt-3 space-y-2 flex-shrink-0">
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={() => setAttested((value) => !value)}
                  className="mt-0.5 w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857]"
                />
                <span>
                  I understand my name and profile link will be shared with {providerName} so they
                  know who referred them.
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareClientName}
                  onChange={() => setShareClientName((value) => !value)}
                  className="mt-0.5 w-4 h-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857]"
                />
                <span>
                  Share my client's first name and last initial with {providerName} (never their
                  contact info)
                </span>
              </label>
            </div>

            <DialogFooter className="flex-row gap-2 sm:gap-2 mt-4 flex-shrink-0">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={selectedCount === 0 || isPending || !attested}
                className="flex-1 bg-[#026857] hover:bg-[#025245]"
              >
                {isPending ? (
                  'Sharing...'
                ) : selectedCount === 0 ? (
                  'Select Clients'
                ) : selectedCount === 1 ? (
                  'Share with 1 Client'
                ) : (
                  `Share with ${selectedCount} Clients`
                )}
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* New Client Tab */}
          <TabsContent value="new" className="flex-1 overflow-y-auto mt-4 pr-2">
            {providerId && (
              <>
                {onSendContactCard && (
                  <div className="mb-4 rounded-lg border bg-background p-3">
                    <p className="text-sm text-gray-600">
                      An invitation asks them to join bQuest. If you would rather just hand over{' '}
                      {providerName}&rsquo;s details, send a contact card — they can save it to
                      their phone without an account.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={onSendContactCard}
                    >
                      Send contact card instead
                    </Button>
                  </div>
                )}
                <NewClientInvitationForm
                  ref={newClientFormRef}
                  referralType="PROVIDER"
                  referralId={providerId}
                  onSuccess={handleNewClientSuccess}
                  onCancel={handleNewClientCancel}
                />
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer - shown for new client tab */}
        {activeTab === 'new' && (
          <DialogFooter className="flex-row gap-2 sm:gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleNewClientCancel}
              disabled={newClientFormRef.current?.isPending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => newClientFormRef.current?.submit()}
              disabled={newClientFormRef.current?.isPending}
              className="flex-1 bg-[#026857] hover:bg-[#025245]"
            >
              {newClientFormRef.current?.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                'Send Invitation'
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
